import { Component, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { Observable, distinctUntilChanged, filter, firstValueFrom, map, startWith } from 'rxjs';
import { InitAction } from '../../store';
import { Store } from '@ngrx/store';
import { AuthService } from '@anecdotes/auth-core';

const SUPPORT_USER_INDICATOR = '_support@anecdotes.ai';

@Component({
  selector: 'app-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss'],
})
export class RootComponent implements OnInit {
  private static isInitialized: boolean;
  protected isNavbarShown$: Observable<boolean>;

  isRealClientOverlayDisplyed: boolean;
  isAuditorSimulationMode: boolean;

  constructor(
    private ngbTooltipConfig: NgbTooltipConfig,
    private router: Router,
    private store: Store,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    // This action should be triggered only one time
    if (!RootComponent.isInitialized) {
      this.store.dispatch(InitAction());
      RootComponent.isInitialized = true;
    }

    this.ngbTooltipConfig.tooltipClass = 'default-tooltip';
    this.ngbTooltipConfig.placement = 'right';
    this.ngbTooltipConfig.triggers = 'hover';
    this.ngbTooltipConfig.autoClose = false;
    this.ngbTooltipConfig.container = 'body';
    this.isNavbarShown$ = this.router.events.pipe(
      filter((routerEvent) => routerEvent instanceof NavigationEnd),
      map(() => this.router.routerState.snapshot.root),
      startWith(this.router.routerState.snapshot.root),
      map((activatedRouteSnapshot) =>
        this.shouldShowNavbar(this.findCurrentActivatedRouteSnapshot(activatedRouteSnapshot)),
      ),
      distinctUntilChanged(),
    );

    this.shouldDisplayRealClientInfoOverlay();
    this.shouldDisplayAuditorSimulationModeOverlay();
  }

  private async shouldDisplayRealClientInfoOverlay(): Promise<void> {
    this.isRealClientOverlayDisplyed = await firstValueFrom(
      this.authService.getUser().pipe(
        filter(Boolean),
        map((res) => res.email?.includes(SUPPORT_USER_INDICATOR)),
      ),
    );
  }

  private async shouldDisplayAuditorSimulationModeOverlay(): Promise<void> {
    this.isAuditorSimulationMode = await firstValueFrom(this.authService.isAuditorSimulationMode());
  }

  private findCurrentActivatedRouteSnapshot(activatedRouteSnapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    if (!activatedRouteSnapshot.firstChild) {
      return activatedRouteSnapshot;
    }

    return this.findCurrentActivatedRouteSnapshot(activatedRouteSnapshot.firstChild);
  }

  private shouldShowNavbar(activatedRouteSnapshot: ActivatedRouteSnapshot): boolean {
    if ('showNavbar' in activatedRouteSnapshot.data) {
      return activatedRouteSnapshot.data.showNavbar;
    }

    if (activatedRouteSnapshot.parent) {
      return this.shouldShowNavbar(activatedRouteSnapshot.parent);
    }

    return false;
  }
}
